import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { GraduationCap, Mail, Lock, AlertCircle, KeyRound, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [step, setStep] = useState('request');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await api.post('/auth/forgot-password', { email: email.trim() });
      setStep('reset');
      toast({
        title: 'Check your email',
        description: 'If this email is registered, a reset code has been sent.',
      });
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Could not send reset code. Try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      await api.post('/auth/reset-password', {
        email: email.trim(),
        otp: otp.trim(),
        new_password: newPassword,
      });
      toast({
        title: 'Password reset',
        description: 'You can now sign in with your new password.',
      });
      navigate('/login');
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Could not reset password. Try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-blue-900 to-background p-4">
      <div className="absolute inset-0 bg-muted/50 opacity-40" />

      <Card className="w-full max-w-md relative z-10 bg-muted/80 backdrop-blur-xl border-border shadow-2xl">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto mb-4 w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center shadow-lg">
            <GraduationCap className="w-9 h-9 text-foreground" />
          </div>
          <CardTitle className="text-2xl font-bold text-foreground">
            {step === 'request' ? 'Forgot password' : 'Reset password'}
          </CardTitle>
          <CardDescription className="text-muted-foreground">
            {step === 'request'
              ? 'Enter your account email to receive a reset code'
              : 'Enter the code from your email and choose a new password'}
          </CardDescription>
        </CardHeader>

        <CardContent>
          {error && (
            <div className="flex items-center gap-2 p-3 mb-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
              <AlertCircle className="w-4 h-4 shrink-0" />
              {error}
            </div>
          )}

          {step === 'request' ? (
            <form onSubmit={handleRequestReset} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="forgot-email" className="text-foreground">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="forgot-email"
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 bg-muted/50 border-border text-foreground placeholder:text-muted-foreground"
                    required
                    data-testid="forgot-email-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-5"
                disabled={loading}
                data-testid="forgot-submit-btn"
              >
                {loading ? 'Sending…' : 'Send reset code'}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div className="flex items-start gap-2 p-3 mb-2 bg-green-500/10 border border-green-500/30 rounded-lg text-green-700 dark:text-green-300 text-sm">
                <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
                <span>If <strong>{email}</strong> is registered, a 6-digit code was sent. It expires in 10 minutes.</span>
              </div>

              <div className="space-y-2">
                <Label htmlFor="forgot-otp" className="text-foreground">Reset code</Label>
                <div className="relative">
                  <KeyRound className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="forgot-otp"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    placeholder="6-digit code"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="pl-10 bg-muted/50 border-border text-foreground placeholder:text-muted-foreground tracking-widest"
                    required
                    data-testid="forgot-otp-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="forgot-new-password" className="text-foreground">New password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="forgot-new-password"
                    type="password"
                    placeholder="At least 6 characters"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="pl-10 bg-muted/50 border-border text-foreground placeholder:text-muted-foreground"
                    required
                    minLength={6}
                    data-testid="forgot-new-password-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="forgot-confirm-password" className="text-foreground">Confirm password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="forgot-confirm-password"
                    type="password"
                    placeholder="Re-enter new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-10 bg-muted/50 border-border text-foreground placeholder:text-muted-foreground"
                    required
                    minLength={6}
                    data-testid="forgot-confirm-password-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-5"
                disabled={loading}
                data-testid="forgot-reset-submit-btn"
              >
                {loading ? 'Resetting…' : 'Reset password'}
              </Button>

              <Button
                type="button"
                variant="ghost"
                className="w-full text-muted-foreground"
                disabled={loading}
                onClick={() => {
                  setStep('request');
                  setOtp('');
                  setNewPassword('');
                  setConfirmPassword('');
                  setError('');
                }}
              >
                Resend code
              </Button>
            </form>
          )}

          <p className="mt-6 text-center">
            <Link
              to="/login"
              className="inline-flex items-center gap-1 text-sm text-sky-400 hover:text-sky-300"
              data-testid="forgot-back-to-login"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to sign in
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default ForgotPassword;
